/* DeliveryIntelligenceTab — platform mix (Zomato/Swiggy/Dunzo) with
 * commission drag visible, hourly volume chart. */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Truck, TrendingDown, Clock } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const PLATFORM_COLORS = {
  zomato: '#E23744',
  swiggy: '#FC8019',
  dunzo: '#4CAF50',
  dine_in: '#1B2A4A',
  takeaway: '#2A7B88',
  room_service: '#A855F7',
};

export default function DeliveryIntelligenceTab({ outletId }) {
  const [mix, setMix] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [m, h] = await Promise.all([
          api.get(`/outlet-intel/${outletId}/delivery/platform-mix?days=30`),
          api.get(`/outlet-intel/${outletId}/delivery/hourly-volume?days=7`),
        ]);
        if (cancelled) return;
        setMix(m.data); setHourly(h.data);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [outletId]);

  if (loading) return <div className="text-sm" style={{ color: '#6B7280' }}>Loading delivery intelligence…</div>;

  const aggregatorRows = (mix?.rows || []).filter(r => r.commission_pct > 0);
  const aggregatorRev = aggregatorRows.reduce((a, r) => a + r.gross_revenue, 0);
  const aggregatorPct = mix?.total_gross ? (aggregatorRev / mix.total_gross * 100).toFixed(1) : 0;
  const maxHourly = Math.max(1, ...(hourly?.rows || []).map(r => r.orders_avg));

  return (
    <div className="space-y-4" data-testid="delivery-intel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Gross (30d)" value={INR(mix?.total_gross)} sub="All channels combined" icon={Truck} testid="kpi-gross" />
        <Kpi label="Aggregator share" value={`${aggregatorPct}%`} sub={`${INR(aggregatorRev)} via Zomato + Swiggy`} icon={Truck} testid="kpi-agg" />
        <Kpi label="Commission lost" value={INR(mix?.total_commission)} sub={`${mix?.total_gross ? ((mix.total_commission / mix.total_gross) * 100).toFixed(1) : 0}% of gross revenue`} icon={TrendingDown} highlight testid="kpi-commission" />
        <Kpi label="Net after commission" value={INR(mix?.total_net_after_commission)} sub="What lands in the bank" icon={Clock} testid="kpi-net" />
      </div>

      {/* Platform rows */}
      <Card title="Platform Mix · Commission-Adjusted Economics (30d)" testid="card-platform">
        <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>{mix?.note}</p>
        <div className="space-y-2">
          {(mix?.rows || []).map(r => (
            <div key={r.channel || 'unknown'} className="rounded-lg p-3" style={{ backgroundColor: '#F7F5F2' }}>
              <div className="flex items-center gap-3">
                <span className="inline-block w-2 h-6 rounded" style={{ backgroundColor: PLATFORM_COLORS[r.channel] || '#6B7280' }} />
                <span className="flex-1 capitalize font-medium text-[13px]">{(r.channel || 'unknown').replace('_', ' ')}</span>
                <span className="text-[11px]" style={{ color: '#6B7280' }}>{r.orders} orders</span>
                <span className="w-24 text-right text-[11px]" style={{ color: '#6B7280' }}>comm {r.commission_pct}%</span>
                <span className="w-28 text-right font-semibold">{INR(r.gross_revenue)}</span>
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2 text-[11px]">
                <Kv k="Commission ₹" v={INR(r.commission_value)} color="#DC2626" />
                <Kv k="Net after comm." v={INR(r.net_after_commission)} />
                <Kv k="Food cost" v={INR(r.food_cost)} />
                <Kv k="Contribution" v={`${INR(r.contribution_margin)} (${r.contribution_pct}%)`} color={r.contribution_pct > 30 ? '#10B981' : r.contribution_pct > 15 ? '#F59E0B' : '#DC2626'} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Hourly volume */}
      <Card title="Aggregator Hourly Volume (7d avg · Zomato + Swiggy + Dunzo)" testid="card-hourly">
        <div className="flex items-end gap-1 h-32">
          {(hourly?.rows || []).map(r => {
            const h = (r.orders_avg / maxHourly) * 100;
            return (
              <div key={r.hour} className="flex-1 flex flex-col items-center justify-end">
                <span className="text-[9px]" style={{ color: '#6B7280' }}>{r.orders_avg || ''}</span>
                <span className="w-full rounded-t" style={{
                  height: `${h}%`,
                  backgroundColor: r.orders_avg >= maxHourly * 0.7 ? '#F59E0B' : '#2A7B88',
                  minHeight: r.orders_avg ? '2px' : '0',
                }} />
                <span className="text-[9px] mt-1" style={{ color: '#9CA3AF' }}>{r.label}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, highlight, icon: Icon, testid }) {
  const bg = highlight ? '#1B2A4A' : 'white';
  const fg = highlight ? 'white' : '#1B2A4A';
  const muted = highlight ? '#9DB5CC' : '#6B7280';
  return (
    <div className="rounded-xl p-4" style={{
      backgroundColor: bg, color: fg, border: highlight ? 'none' : '1px solid #E5E1DC',
    }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={12} color={muted} />}
        <p className="text-[10px] uppercase tracking-wider" style={{ color: muted }}>{label}</p>
      </div>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-[11px] mt-1" style={{ color: muted }}>{sub}</p>
    </div>
  );
}

function Kv({ k, v, color }) {
  return (
    <div>
      <p className="text-[9px] uppercase" style={{ color: '#9CA3AF' }}>{k}</p>
      <p className="font-medium" style={{ color: color || '#1B2A4A' }}>{v}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }} data-testid={testid}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}
